import { Button, Center, Stack, Text, Title } from '@mantine/core';
import { useForm, zodResolver } from '@mantine/form';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { z } from 'zod';

import { useForgotPassword } from '@/api/generated/registration/registration';
import { IconFigmaArrowLeft, IconFigmaMail } from '@/assets/icons';
import { FormErrorMessage, MutationButton, TextField } from '@/components/common';
import { AuthCard } from '@/features/auth/components/AuthCard';
import { brandTextStyle } from '@/features/auth/components/authFieldStyles';
import { authErrorMessage } from '@/features/auth/utils/authErrorMessage';

const schema = z.object({
  email: z.string().email()
});

type FormValues = z.infer<typeof schema>;

export const ForgotPasswordForm = () => {
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);

  // Backend uvijek vraća 200 s generičkom porukom, bez obzira postoji li
  // email u sustavu (sprječava enumeraciju adresa) — jedina realna greška
  // ovdje je mrežna/serverska, ne "email ne postoji".
  const forgotPasswordMutation = useForgotPassword({
    mutation: {
      onSuccess: () => setSubmitted(true)
    }
  });

  const form = useForm<FormValues>({
    validate: zodResolver(schema),
    initialValues: { email: '' }
  });

  const handleSubmit = (values: FormValues) => {
    forgotPasswordMutation.mutate({ data: { email: values.email } });
  };

  if (submitted) {
    return (
      <AuthCard px={32} py={36} w={{ base: '100%', md: 420 }}>
        <Stack align="center" gap={14}>
          <Center
            w={56}
            h={56}
            style={{
              borderRadius: 12,
              backgroundColor: 'var(--color-accent-secondary-soft)',
              color: 'var(--color-icon-accent)'
            }}
          >
            <IconFigmaMail size={24} />
          </Center>
          <Title order={2} fz={24} fw={700} ta="center" style={brandTextStyle}>
            {t('forgotPassword.successTitle')}
          </Title>
          <Text size="sm" ta="center" style={{ color: 'var(--color-text-secondary)' }}>
            {t('forgotPassword.successMessage')}
          </Text>
          <Text fz={10} ta="center" opacity={0.85} style={{ color: 'var(--color-text-muted)' }}>
            {t('forgotPassword.successNote')}
          </Text>
          <Button
            component={Link}
            to="/login"
            fullWidth
            leftSection={<IconFigmaArrowLeft size={15} />}
          >
            {t('forgotPassword.backToLoginButton')}
          </Button>
        </Stack>
      </AuthCard>
    );
  }

  return (
    <AuthCard p={32} w={{ base: '100%', md: 440 }}>
      <Stack gap="md">
        <Title order={2} fz={24} fw={700} style={brandTextStyle}>
          {t('forgotPassword.title')}
        </Title>
        <Text size="sm" style={{ color: 'var(--color-text-secondary)' }}>
          {t('forgotPassword.subtitle')}
        </Text>

        {(forgotPasswordMutation.isError || forgotPasswordMutation.isPaused) && (
          <FormErrorMessage
            message={authErrorMessage(
              forgotPasswordMutation.error,
              forgotPasswordMutation.isPaused,
              t,
              () => t('forgotPassword.error')
            )}
          />
        )}

        <form onSubmit={form.onSubmit(handleSubmit)} noValidate>
          <Stack gap="md">
            <TextField label={t('auth.email')} required {...form.getInputProps('email')} />

            <MutationButton
              type="submit"
              fullWidth
              isPending={forgotPasswordMutation.isPending}
              isPaused={forgotPasswordMutation.isPaused}
            >
              {t('forgotPassword.submit')}
            </MutationButton>

            <Button
              component={Link}
              to="/login"
              fullWidth
              variant="outline"
              leftSection={<IconFigmaArrowLeft size={15} />}
            >
              {t('auth.login')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </AuthCard>
  );
};
